import React, { useState } from 'react';
import axios from 'axios';
import { Mail, Clock, CheckCircle2, MapPin, Phone } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useToast } from '../hooks/use-toast';
import { Reveal } from '../components/shared';
import { PageHeader } from './Flooring';
import { business, categories } from '../mock/mock';

export default function Estimate() {
  const { toast } = useToast();
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    firstName: '', lastName: '', email: '', phone: '', zip: '', contactMethod: 'Call Me', interest: '', message: ''
  });

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/estimates`, form);
      setSubmitted(true);
      toast({ title: 'Request received!', description: 'A flooring expert will reach out shortly.' });
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      toast({ title: 'Something went wrong', description: 'Please try again or call us directly.', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <PageHeader
        eyebrow="Free Estimate"
        title="Request your free in-home estimate"
        sub="Tell us a little about your project and we’ll bring the samples, take the measurements, and give you an honest quote — all at no cost."
        image="https://images.unsplash.com/photo-1649083048770-82e8ffd80431?auto=format&fit=crop&w=1920&q=80"
      />

      <section className="max-w-7xl mx-auto px-6 py-16 md:py-20 grid lg:grid-cols-5 gap-10">
        {/* form */}
        <div className="lg:col-span-3">
          {submitted ? (
            <Reveal>
              <div className="bg-white rounded-2xl border border-brand-line shadow-sm p-10 text-center">
                <CheckCircle2 size={56} className="mx-auto text-brand-forest" />
                <h2 className="font-display text-3xl font-semibold text-brand-charcoal mt-5">Thank you!</h2>
                <p className="text-brand-stone mt-3 max-w-md mx-auto text-[17px] leading-relaxed">Your request has been received. Your local flooring expert will reach out shortly to schedule your free in-home visit.</p>
                <Button onClick={() => { setSubmitted(false); setForm({ firstName: '', lastName: '', email: '', phone: '', zip: '', contactMethod: 'Call Me', interest: '', message: '' }); }} className="mt-7 bg-brand-clay hover:bg-brand-clayDark text-white rounded-full px-7 h-12">Submit Another Request</Button>
              </div>
            </Reveal>
          ) : (
            <form onSubmit={submit} className="bg-white rounded-2xl border border-brand-line shadow-sm p-7 md:p-9 space-y-5">
              <h2 className="font-display text-2xl font-semibold text-brand-charcoal">Project details</h2>
              <div className="grid sm:grid-cols-2 gap-5">
                <div className="space-y-2">
                  <Label htmlFor="firstName">First name *</Label>
                  <Input id="firstName" required value={form.firstName} onChange={(e) => update('firstName', e.target.value)} placeholder="Jane" className="h-12" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="lastName">Last name *</Label>
                  <Input id="lastName" required value={form.lastName} onChange={(e) => update('lastName', e.target.value)} placeholder="Doe" className="h-12" />
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-5">
                <div className="space-y-2">
                  <Label htmlFor="email">Email address *</Label>
                  <Input id="email" type="email" required value={form.email} onChange={(e) => update('email', e.target.value)} placeholder="jane@email.com" className="h-12" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="phone">Phone number *</Label>
                  <Input id="phone" required value={form.phone} onChange={(e) => update('phone', e.target.value)} placeholder="(571) 555-0123" className="h-12" />
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-5">
                <div className="space-y-2">
                  <Label htmlFor="zip">Postal code *</Label>
                  <Input id="zip" required value={form.zip} onChange={(e) => update('zip', e.target.value)} placeholder="22301" className="h-12" />
                </div>
                <div className="space-y-2">
                  <Label>Flooring interest</Label>
                  <Select value={form.interest} onValueChange={(v) => update('interest', v)}>
                    <SelectTrigger className="h-12"><SelectValue placeholder="Select a type" /></SelectTrigger>
                    <SelectContent>
                      {categories.map((c) => <SelectItem key={c.slug} value={c.name}>{c.name}</SelectItem>)}
                      <SelectItem value="Not sure yet">Not sure yet</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-2">
                <Label>How should we contact you? *</Label>
                <Select value={form.contactMethod} onValueChange={(v) => update('contactMethod', v)}>
                  <SelectTrigger className="h-12"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Call Me">Call Me</SelectItem>
                    <SelectItem value="Email Me">Email Me</SelectItem>
                    <SelectItem value="Text Me">Text Me</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="message">Tell us about your project</Label>
                <Textarea id="message" rows={4} value={form.message} onChange={(e) => update('message', e.target.value)} placeholder="Which rooms, approximate square footage, timeline..." />
              </div>
              <Button type="submit" disabled={loading} className="w-full bg-brand-clay hover:bg-brand-clayDark text-white rounded-full h-14 text-base font-medium">{loading ? 'Sending…' : 'Request My Free Estimate'}</Button>
              <p className="text-xs text-brand-stone text-center">By submitting, you agree to be contacted about your flooring project. No spam, ever.</p>
            </form>
          )}
        </div>

        {/* sidebar */}
        <div className="lg:col-span-2 space-y-6">
          <Reveal>
            <div className="bg-brand-charcoal rounded-2xl p-8 text-brand-ivory">
              <h3 className="font-display text-2xl font-semibold">Prefer to talk?</h3>
              <p className="text-brand-sand/80 mt-2 text-[15px]">Reach us directly — we’re happy to answer questions and book your visit.</p>
              <div className="mt-6 space-y-4">
                <a href={business.phoneHref} className="flex items-center gap-3 hover:text-brand-clay transition-colors"><Phone size={18} className="text-brand-clay" /> {business.phone}</a>
                <a href={`mailto:${business.email}`} className="flex items-center gap-3 hover:text-brand-clay transition-colors"><Mail size={18} className="text-brand-clay" /> {business.email}</a>
                <p className="flex items-start gap-3 text-brand-sand/80"><MapPin size={18} className="text-brand-clay mt-0.5" /> {business.serviceArea}</p>
              </div>
            </div>
          </Reveal>
          <Reveal delay={100}>
            <div className="bg-white rounded-2xl border border-brand-line shadow-sm p-8">
              <h3 className="font-display text-xl font-semibold text-brand-charcoal flex items-center gap-2"><Clock size={18} className="text-brand-clay" /> Business hours</h3>
              <ul className="mt-4 space-y-2 text-sm">
                {business.hours.map((h) => (
                  <li key={h.day} className="flex justify-between text-brand-stone">
                    <span className="font-medium text-brand-charcoal">{h.day}</span><span>{h.time}</span>
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
